import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Clock } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { Eyebrow } from "@/components/ServiceSections";
import { api } from "@/lib/api";
import { IMAGES } from "@/content/services";

export const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });

export const PostCard = ({ post, delay = 0 }) => (
  <Reveal delay={delay}>
    <Link
      to={`/insights/${post.slug}`}
      className="glass-card group flex h-full flex-col p-5 sm:p-7"
      data-testid={`insight-card-${post.slug}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {(post.tags || []).slice(0, 3).map((t) => (
          <span
            key={t}
            className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8E9AEA]"
          >
            {t}
          </span>
        ))}
      </div>
      <h3 className="mt-4 font-display text-lg font-bold leading-snug text-white sm:text-xl">{post.title}</h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[#DDE3F2]/90">{post.excerpt}</p>
      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-white/10 pt-4 text-[11px] uppercase tracking-[0.14em] text-[#A6B0C9]">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-3.5 w-3.5" />
          {fmtDate(post.publishedAt)}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          {post.readingMinutes} min read
        </span>
        <span className="ml-auto inline-flex items-center gap-1.5 font-semibold text-white">
          Read
          <ArrowUpRight className="h-3.5 w-3.5 text-[#8E9AEA] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </span>
      </div>
    </Link>
  </Reveal>
);

export const useInsights = () => {
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  React.useEffect(() => {
    let alive = true;
    api
      .get("/posts")
      .then((r) => alive && setPosts(r.data))
      .catch(() => alive && setPosts([]))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);
  return { posts, loading };
};

export default function Insights() {
  const { posts, loading } = useInsights();

  return (
    <div data-testid="insights-page">
      <PageHero
        breadcrumb={["Home", "Insights"]}
        pill="Insights"
        titleLines={[[{ t: "Engineering notes on " }, { t: "compliance and defence.", accent: true }]]}
        description="Short, technical articles from the Lanvaus team on DPDP Act 2023 readiness, CERT-In directions, SOC operations and cloud security. Written for the people who have to implement it, not the people who sign it off."
        primaryCta={{ label: "Talk to an Engineer", to: "/contact" }}
        secondaryCta={{ label: "Download Resources", to: "/resources" }}
        image={IMAGES.matrix}
        imageAlt="Security engineer reviewing log data and compliance evidence on screen"
        showCompliance={false}
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-24 lg:px-16" data-testid="insights-list">
        <Reveal>
          <Eyebrow>Latest</Eyebrow>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {loading ? "Loading articles" : `${posts.length} article${posts.length === 1 ? "" : "s"}`},{" "}
            <span className="text-[#8E9AEA]">no vendor fluff.</span>
          </h2>
        </Reveal>
        {!loading && posts.length === 0 && (
          <p className="mt-8 text-sm text-[#A6B0C9]" data-testid="insights-empty">
            No articles published yet. Check back shortly.
          </p>
        )}
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {posts.map((p, i) => (
            <PostCard key={p.slug} post={p} delay={Math.min(i, 4) * 0.06} />
          ))}
        </div>
      </section>

      <CTABand
        title={{ pre: "Want this applied to ", accent: "your environment?" }}
        copy="Send us your current setup and a Lanvaus engineer will tell you, honestly, where the gaps are against DPDP and the CERT-In directions."
      />
    </div>
  );
}
